import { useQuery } from "@apollo/client/react";
import { useEffect, useMemo, useRef, useState } from "react";
import type { ReactNode } from "react";
import { Link, useNavigate } from "react-router";
import { FormattedMessage, useIntl } from "react-intl";
import { userErrorMessage } from "@/lib/user-error";
import { NoBackend } from "@/components/common/NoBackend";
import { Button } from "@/components/design/Button";
import { buttonClasses } from "@/components/design/button-classes";
import { SegmentedControl } from "@/components/design/SegmentedControl";
import { ShareCover, ShareIcon, ShareItemCard } from "@/components/share/ShareItemCard";
import { ShareUrlButton } from "@/components/share/ShareUrlButton";
import { MySharedItemsDocument, SharedItemsDocument } from "@/graphql/generated/operations";
import { fmtCount } from "@/lib/share-item";
import { useAssistantContext } from "@/lib/use-assistant-context";
import { useAssistantTour } from "@/lib/use-assistant-tour";
import { Hero } from "@/components/layout/Hero";

// SHARE landing - node-by-node port of workshop's screens/Share.jsx.
// Hero + Entdecken carousel (4 curated top-5 lists) + 'Beobachtet &
// Beteiligt' (items the user pinned or signed up to) + 'Meine Projekte
// & Posts' (own items). The last two are horizontally-scrolling rows.

const CAROUSELS = [
  { id: "new" as const, icon: "clock" as const, labelId: "share.landing.carouselNew" as const, labelDefault: "Newest" },
  { id: "top" as const, icon: "star" as const, labelId: "share.landing.carouselTop" as const, labelDefault: "Top rated" },
  { id: "interest" as const, icon: "users" as const, labelId: "share.landing.carouselInterest" as const, labelDefault: "Most interested" },
  { id: "discussed" as const, icon: "comment" as const, labelId: "share.landing.carouselDiscussed" as const, labelDefault: "Most comments" },
];
type CarouselMode = (typeof CAROUSELS)[number]["id"];

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type Item = any;

export function SharePage() {
  const navigate = useNavigate();
  const intl = useIntl();

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const allInput: any = { take: 50 };
  const { data, loading, error } = useQuery(SharedItemsDocument, { variables: { input: allInput } });

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const myInput: any = { first: 20, where: { owner: { eq: true } } };
  const { data: myData, loading: myLoading, error: myError } = useQuery(MySharedItemsDocument, {
    variables: { input: myInput },
  });
  const myItems = (myData?.sharedItems?.nodes ?? []) as Item[];

  // 'Beobachtet & Beteiligt' = pinned (Beobachtet) oder signedUp (Interessent) -
  // beide sind die einzigen aktivitaetsbezogenen Per-User-Flags, die das Schema
  // filterbar macht (kein dediziertes watched-Flag, vgl. ShareItemCard chips).
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const activityInput: any = {
    take: 8,
    where: { or: [{ pinned: { eq: true } }, { signedUp: { eq: true } }] },
  };
  const {
    data: activityData,
    loading: activityLoading,
    error: activityError,
  } = useQuery(SharedItemsDocument, { variables: { input: activityInput } });
  const activityItems = (activityData?.sharedItems?.nodes ?? []) as Item[];

  const [mode, setMode] = useState<CarouselMode>("new");
  const [idx, setIdx] = useState(0);

  const lists: Record<CarouselMode, Item[]> = useMemo(() => {
    const items = (data?.sharedItems?.nodes ?? []) as Item[];
    const ts = (d: string | null | undefined) => (d ? Date.parse(d) || 0 : 0);
    const by = (fn: (a: Item, b: Item) => number) => [...items].sort(fn).slice(0, 5);
    return {
      top: by((a, b) => (b.rating ?? 0) - (a.rating ?? 0)),
      new: by((a, b) => ts(b.publishedOn) - ts(a.publishedOn)),
      interest: by((a, b) => (b.signUps ?? 0) - (a.signUps ?? 0)),
      discussed: by((a, b) => (b.comments ?? 0) - (a.comments ?? 0)),
    };
  }, [data?.sharedItems?.nodes]);

  const list = lists[mode] ?? [];
  const featured = list[idx] ?? list[0];
  const activeCarousel = CAROUSELS.find((c) => c.id === mode);

  const selectMode = (m: CarouselMode) => {
    setMode(m);
    setIdx(0);
  };
  const go = (delta: number) =>
    setIdx((i) => (list.length ? (i + delta + list.length) % list.length : 0));

  // idx is in the deps so the countdown restarts on every slide change -
  // including manual ones (arrows, dots, tab switch). Without it a manual jump
  // could be cut short by an about-to-fire tick. Effectively a 7s timeout that
  // re-arms after each change.
  useEffect(() => {
    if (list.length < 2) return undefined;
    const t = setInterval(() => setIdx((i) => (i + 1) % list.length), 7000);
    return () => clearInterval(t);
  }, [mode, list.length, idx]);

  // Live assistant context (motive 1, ADR 0020): the Share feed the user is browsing.
  useAssistantContext(
    data
      ? [
          {
            description: "the Share community feed",
            value:
              `browsing the "${mode}" list` +
              (featured ? `; featured item: "${featured.name}"` : "") +
              `; ${myItems.length} of the shown items are the user's own`,
          },
        ]
      : null,
  );

  // Guided tour (/tour): the Share landing — discover carousel, create CTA, activity + own items.
  useAssistantTour(
    data
      ? {
          label: intl.formatMessage({
            id: "share.landing.tour.label",
            defaultMessage: "Share",
          }),
          summary: featured
            ? intl.formatMessage(
                {
                  id: "share.landing.tour.summary",
                  defaultMessage:
                    "This is Share, the community feed where people publish projects and posts, gather interested people and feedback. Right now \"{name}\" is in the spotlight.",
                },
                { name: featured.name },
              )
            : intl.formatMessage({
                id: "share.landing.tour.emptySummary",
                defaultMessage:
                  "This is Share, the community feed where people publish projects and posts, gather interested people and feedback.",
              }),
          stops: [
            {
              anchor: "share.carousel",
              title: intl.formatMessage({
                id: "share.landing.tour.carousel.title",
                defaultMessage: "Discover",
              }),
              description: intl.formatMessage(
                {
                  id: "share.landing.tour.carousel.desc",
                  defaultMessage:
                    "Four curated top-5 lists: top rated, newest, most interested, most comments. Switch lists with the tabs; currently showing \"{featured}\". Click Open to jump into an item.",
                },
                { featured: featured?.name ?? "–" },
              ),
            },
            {
              anchor: "share.create",
              title: intl.formatMessage({
                id: "share.landing.tour.create.title",
                defaultMessage: "Create something",
              }),
              description: intl.formatMessage({
                id: "share.landing.tour.create.desc",
                defaultMessage:
                  "Publish your own project or post here. Describe it, add images, and collect interested people.",
              }),
            },
            {
              anchor: "share.activity",
              title: intl.formatMessage({
                id: "share.landing.tour.activity.title",
                defaultMessage: "Watched & involved",
              }),
              description:
                activityItems.length === 0
                  ? intl.formatMessage({
                      id: "share.landing.tour.activity.emptyDesc",
                      defaultMessage:
                        "Items you watch or sign up for will collect here; nothing yet. Open an item and hit Watch or Express interest.",
                    })
                  : intl.formatMessage(
                      {
                        id: "share.landing.tour.activity.desc",
                        defaultMessage:
                          "The {count} items you are watching or signed up for, so you can get back to them quickly.",
                      },
                      { count: activityItems.length },
                    ),
            },
            {
              anchor: "share.mine",
              title: intl.formatMessage({
                id: "share.landing.tour.mine.title",
                defaultMessage: "My projects & posts",
              }),
              description:
                myItems.length === 0
                  ? intl.formatMessage({
                      id: "share.landing.tour.mine.emptyDesc",
                      defaultMessage:
                        "Your own published items will appear here. You haven't created any yet.",
                    })
                  : intl.formatMessage(
                      {
                        id: "share.landing.tour.mine.desc",
                        defaultMessage:
                          "Your own {count} published items. 'View all' opens the full list where you can manage them.",
                      },
                      { count: myItems.length },
                    ),
            },
          ],
        }
      : null,
  );

  return (
    <main
      className="w-full max-w-[1600px] px-4 pb-12 pt-6 @sm:px-6 @lg:px-10"
      data-graphql="SharedItems, MySharedItems"
    >
      <Hero
        title="SHARE"
        subtitle={
          <>
            <FormattedMessage
              id="share.landing.heroSubtitleLine1"
              defaultMessage="Show what you are working on."
            />
            <br />
            <FormattedMessage
              id="share.landing.heroSubtitleLine2"
              defaultMessage="Find who joins in."
            />
          </>
        }
        body={intl.formatMessage({
          id: "share.landing.heroBody",
          defaultMessage:
            "Publish projects and posts, gather interested people, feedback and reach. And discover what the community is currently building.",
        })}
        image={{ src: "/heroes/match.png" }}
        cta={{
          kind: "link",
          label: intl.formatMessage({ id: "share.landing.heroCta", defaultMessage: "Create ↗" }),
          to: "/share/create",
          anchor: "share.create",
        }}
      />

      <div className="mt-10">
        <div className="mb-3 flex items-baseline justify-between gap-3">
          <div className="text-[10px] tracking-[0.25em] text-ink/55">
            <FormattedMessage id="share.landing.discoverEyebrow" defaultMessage="Discover" />
          </div>
          <button
            type="button"
            onClick={() => navigate("/extended-search?type=SHARE")}
            className="text-[10px] uppercase tracking-[0.2em] text-ink/40 transition hover:text-ink"
          >
            <FormattedMessage
              id="share.landing.discoverAllPosts"
              defaultMessage="Discover all posts ↗"
            />
          </button>
        </div>
        <div className="box relative overflow-hidden bg-surface" data-assistant-anchor="share.carousel">
          {/* Fixed height on desktop: the box must not grow with the image
              or with wrapping copy; the cover crops via object-cover. */}
          <div className="grid grid-cols-1 @lg:h-[400px] @lg:grid-cols-[minmax(0,1.25fr)_minmax(280px,0.75fr)]">
            {/* Flex column inside the fixed-height box: the action row is
                pinned via mt-auto and the description is the flexible,
                clamped element — when a two-line title eats the budget, copy
                gives way, never the buttons. */}
            <div className="flex min-h-0 flex-col p-5 @sm:p-8 @lg:p-6">
              <div className="mb-4 flex shrink-0 flex-wrap items-center gap-3">
                <SegmentedControl
                  className="flex-wrap"
                  optionClassName="inline-flex items-center gap-1.5 tracking-[0.15em]"
                  value={mode}
                  onChange={selectMode}
                  options={CAROUSELS.map((c) => ({
                    value: c.id,
                    label: (
                      <>
                        <ShareIcon name={c.icon} />{" "}
                        {intl.formatMessage({ id: c.labelId, defaultMessage: c.labelDefault })}
                      </>
                    ),
                  }))}
                />
                {loading ? (
                  <span className="ml-auto text-[10px] tracking-[0.2em] text-ink/40">
                    <FormattedMessage id="share.landing.loading" defaultMessage="loading…" />
                  </span>
                ) : null}
                {error ? (
                  <span className="ml-auto text-[10px] tracking-[0.2em] text-ink/40">
                    <FormattedMessage
                      id="share.landing.error"
                      defaultMessage="Error: {message}"
                      values={{ message: userErrorMessage(intl, error) }}
                    />
                  </span>
                ) : null}
              </div>
              {activeCarousel ? (
                <div className="mb-2 inline-flex shrink-0 items-center gap-1.5 self-start text-[9px] tracking-[0.25em] text-accent">
                  <ShareIcon name={activeCarousel.icon} />{" "}
                  {intl.formatMessage({
                    id: activeCarousel.labelId,
                    defaultMessage: activeCarousel.labelDefault,
                  })}
                  {list.length > 0 ? (
                    <span className="text-ink/30">
                      · {idx + 1}/{list.length}
                    </span>
                  ) : null}
                </div>
              ) : null}
              {/* pb gives descenders (g, j, p) room: line-clamp implies
                  overflow-hidden, which clips ink below the tight 0.95
                  line box without it. */}
              <h1 className="line-clamp-2 min-h-[2.05em] shrink-0 pb-[0.15em] font-serif text-4xl leading-[0.95] tracking-tight @sm:text-5xl @xl:text-6xl">
                {featured?.name ?? "–"}
              </h1>
              {/* Title and subtext each reserve a FIXED two-line space
                  (min-h = 2 lines + line-clamp-2), so 1- vs 2-line copy occupies
                  the same height and the stats/buttons below never shift between
                  slides. Longer copy clamps with a trailing "…". */}
              <p className="mt-3 min-h-[3.25em] shrink-0 line-clamp-2 max-w-xl text-sm leading-relaxed text-ink/70">
                {featured
                  ? featured.shortDescription ||
                    intl.formatMessage({
                      id: "share.landing.noShortDescription",
                      defaultMessage: "No short description.",
                    })
                  : loading
                    ? intl.formatMessage({ id: "share.landing.loading", defaultMessage: "loading…" })
                    : intl.formatMessage({
                        id: "share.landing.noSharedItems",
                        defaultMessage: "No shared items available.",
                      })}
              </p>
              <div className="mt-4 flex shrink-0 flex-wrap items-end gap-5 @sm:gap-8">
                <Big
                  n={fmtCount(featured?.views)}
                  l={intl.formatMessage({ id: "share.landing.statViews", defaultMessage: "Views" })}
                />
                <Big
                  n={fmtCount(featured?.ratings)}
                  l={intl.formatMessage({
                    id: "share.landing.statRatings",
                    defaultMessage: "Ratings",
                  })}
                />
                <Big
                  n={fmtCount(featured?.signUps)}
                  l={intl.formatMessage({
                    id: "share.landing.statInterested",
                    defaultMessage: "Interested",
                  })}
                />
                <Big
                  n={fmtCount(featured?.comments)}
                  l={intl.formatMessage({
                    id: "share.landing.statComments",
                    defaultMessage: "Comments",
                  })}
                />
              </div>
              <div className="mt-auto flex shrink-0 flex-wrap gap-2 pt-4">
                <ShareUrlButton
                  path={featured?.id ? `/share/discover/${featured.id}` : "/share"}
                  label={intl.formatMessage({
                    id: "share.landing.share",
                    defaultMessage: "↗ Share",
                  })}
                  className={buttonClasses({ variant: "secondary", size: "compact" })}
                />
                {featured?.id ? (
                  <Link
                    to={`/share/discover/${featured.id}`}
                    className={buttonClasses({ variant: "primary", size: "compact" })}
                  >
                    <FormattedMessage id="share.landing.open" defaultMessage="Open ↗" />
                  </Link>
                ) : (
                  <Button
                    variant="primary"
                    size="compact"
                    disabled
                    className="disabled:opacity-40 cursor-not-allowed"
                  >
                    <FormattedMessage id="share.landing.open" defaultMessage="Open ↗" />
                  </Button>
                )}
              </div>
            </div>
            <div className="relative min-h-[260px] @lg:min-h-[300px]">
              <div className="absolute inset-0">
                <ShareCover item={featured} index={idx} className="h-full w-full" />
              </div>
            </div>
          </div>
        </div>
      </div>

      {list.length > 1 ? (
        <div className="mt-4 flex items-center justify-center gap-3">
          <button
            onClick={() => go(-1)}
            aria-label={intl.formatMessage({
              id: "share.landing.previous",
              defaultMessage: "Previous",
            })}
            className="smycles-back-button text-lg leading-none"
          >
            ‹
          </button>
          <div className="flex items-center gap-1.5">
            {list.map((_, i) => (
              <button
                key={i}
                onClick={() => setIdx(i)}
                aria-label={intl.formatMessage(
                  { id: "share.landing.slide", defaultMessage: "Slide {number}" },
                  { number: i + 1 },
                )}
                className={`h-1.5 rounded-full transition-all ${
                  i === idx ? "w-5 bg-accent" : "w-1.5 bg-ink/20 hover:bg-ink/40"
                }`}
              />
            ))}
          </div>
          <button
            onClick={() => go(1)}
            aria-label={intl.formatMessage({ id: "share.landing.next", defaultMessage: "Next" })}
            className="smycles-back-button text-lg leading-none"
          >
            ›
          </button>
        </div>
      ) : null}

      <Section
        label={intl.formatMessage({
          id: "share.landing.watchedSection",
          defaultMessage: "Watched & involved",
        })}
        right={intl.formatMessage({
          id: "share.landing.myActivity",
          defaultMessage: "My activity",
        })}
        anchor="share.activity"
      >
        {!activityLoading && !activityError && activityItems.length === 0 ? (
          <NoBackend
            label={intl.formatMessage({
              id: "share.landing.nothingWatched",
              defaultMessage: "Nothing watched or involved yet",
            })}
            hint={intl.formatMessage({
              id: "share.landing.nothingWatchedHint",
              defaultMessage: "save a project or apply",
            })}
          />
        ) : (
          <Row>
            {activityItems.map((p, i) => (
              <Link
                key={p.id}
                to={`/share/discover/${p.id}`}
                className="block w-64 shrink-0 transition hover:-translate-y-0.5"
              >
                <ShareItemCard item={p} index={i} />
              </Link>
            ))}
          </Row>
        )}
      </Section>

      <Section
        label={intl.formatMessage({
          id: "share.landing.myProjectsSection",
          defaultMessage: "My projects & posts",
        })}
        right={intl.formatMessage({
          id: "share.landing.viewAll",
          defaultMessage: "View all ↗",
        })}
        to="/share/mine"
        anchor="share.mine"
      >
        {!myLoading && !myError && myItems.length === 0 ? (
          <NoBackend
            label={intl.formatMessage({
              id: "share.landing.noOwnPosts",
              defaultMessage: "No own posts yet",
            })}
            hint={intl.formatMessage({
              id: "share.landing.noOwnPostsHint",
              defaultMessage: "create a project or a post",
            })}
          />
        ) : (
          <Row>
            {myItems.map((p, i) => (
              <Link
                key={p.id}
                to={`/share/discover/${p.id}`}
                className="block w-64 shrink-0 transition hover:-translate-y-0.5"
              >
                <ShareItemCard item={p} index={i} />
              </Link>
            ))}
          </Row>
        )}
      </Section>
    </main>
  );
}

// Horizontal scroll row (mirrors HomePage's Row): vertical mousewheel over the
// area becomes horizontal scroll when the row overflows and the gesture is
// vertical-dominant.
function Row({ children }: { children: ReactNode }) {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    const onWheel = (e: WheelEvent) => {
      if (el.scrollWidth <= el.clientWidth) return;
      if (Math.abs(e.deltaY) <= Math.abs(e.deltaX)) return;
      el.scrollLeft += e.deltaY;
      e.preventDefault();
    };
    el.addEventListener("wheel", onWheel, { passive: false });
    return () => el.removeEventListener("wheel", onWheel);
  }, []);
  return (
    <div ref={ref} className="flex gap-4 overflow-x-auto pb-1">
      {children}
    </div>
  );
}

function Big({ n, l, suf }: { n: string; l: string; suf?: string }) {
  return (
    <div>
      <div className="font-serif text-3xl leading-none">
        {n}
        {suf ? <span className="text-base text-ink/55">{suf}</span> : null}
      </div>
      <div className="mt-1 text-[10px] tracking-[0.2em] text-ink/55">{l}</div>
    </div>
  );
}

function Section({
  label,
  right,
  to,
  anchor,
  children,
}: {
  label: string;
  right: string;
  to?: string;
  /** Assistant anchor (HighlightElement tool + /tour) — on the section root, so the whole
   *  region gets the highlight outline. */
  anchor?: string;
  children: ReactNode;
}) {
  return (
    <div className="mt-10" data-assistant-anchor={anchor}>
      <div className="mb-3 flex items-baseline justify-between">
        <div className="text-[10px] tracking-[0.25em] text-ink/55">{label}</div>
        {to ? (
          <Link
            to={to}
            className="text-[10px] uppercase tracking-[0.2em] text-ink/40 transition hover:text-ink"
          >
            {right}
          </Link>
        ) : (
          <div className="text-[10px] tracking-[0.2em] text-ink/40">{right}</div>
        )}
      </div>
      {children}
    </div>
  );
}
